import React from 'react';
import { Modal, ModalBody, Button } from 'reactstrap';
import { IconGift, IconExternalLink } from '@tabler/icons-react';
import { useMemoryGameStore } from '../../../stores/index.js';
import { renderMarkdownToReact } from '@core/frontend/ui';
import { resolveThemeColor, resolveButtonColor, resolveButtonTextColor } from './colorUtils';
import styles from './WinModal.module.scss';

const WinModal = ({ backdropClassName }) => {
    const { isGameCompleted, hasTicket, matchedPairsCount, totalPairsCount, attempts, config } = useMemoryGameStore();

    const isOpen = isGameCompleted && (config?.winModal?.show ?? true);

    if (!isOpen) return null;

    const winCta = config?.winModal;
    const buttonConfig = winCta?.button;
    const cardColorType = config?.cards?.colorType;
    const cardCustomColor = config?.cards?.customColor;

    const rawUrl = buttonConfig?.link;
    const hasLink = rawUrl && rawUrl !== '#';
    const destinationUrl = hasLink ? rawUrl : null;
    const isExternal =
        destinationUrl && (destinationUrl.startsWith('http://') || destinationUrl.startsWith('https://'));
    const linkTarget = isExternal ? '_blank' : undefined;
    const linkRel = isExternal ? 'noopener' : undefined;

    const resolvedThemeColor = resolveThemeColor(cardColorType, cardCustomColor);

    const modalStyle =
        !hasTicket && resolvedThemeColor
            ? {
                  backgroundColor: resolvedThemeColor,
              }
            : {};

    const btnColorType = buttonConfig?.colorType;
    const btnCustomColor = buttonConfig?.customColor;

    const buttonColor = resolveButtonColor(btnColorType, btnCustomColor);

    const isDarkBg =
        btnColorType === 'primary' ||
        btnColorType === 'secondary' ||
        (btnColorType === 'custom' && buttonColor !== '#f5a623');

    const textColorType = buttonConfig?.textColorType;
    const textCustomColor = buttonConfig?.textCustomColor;

    const defaultBtnTextColor = isDarkBg ? '#ffffff' : 'var(--bs-dark, #212529)';

    const resolvedBtnTextColor = resolveButtonTextColor(textColorType, textCustomColor, defaultBtnTextColor);

    const buttonStyle = {
        ...(buttonColor ? { backgroundColor: buttonColor } : {}),
        ...(textColorType || buttonColor ? { color: resolvedBtnTextColor } : {}),
    };

    return (
        <Modal
            isOpen={isOpen}
            centered
            keyboard={false}
            className="mx-auto"
            contentClassName="border-0 shadow-lg"
            backdrop="static"
            backdropClassName={backdropClassName}
            style={{ maxWidth: '360px' }}
        >
            <ModalBody
                className={`${styles.modalContent} ${hasTicket ? styles.specialWin : ''} text-center p-4 d-flex flex-column align-items-center gap-3 rounded-3`}
                style={modalStyle}
            >
                <div className="d-flex align-items-center justify-content-center my-1">
                    <IconGift size={36} stroke="var(--bs-dark, #212529)" strokeWidth={2.5} />
                </div>

                <div className="w-100">
                    <h2 className={`h5 mb-2 fw-bold ${hasTicket ? 'text-dark' : 'text-white'}`}>
                        {winCta?.headline || 'Herzlichen Glückwunsch!'}
                    </h2>
                    <p className={`small mb-0 ${hasTicket ? 'text-dark' : 'text-white'}`}>
                        {renderMarkdownToReact(
                            winCta?.copy || 'Du hast alle Paare erfolgreich gefunden. Gut gespielt!'
                        )}
                    </p>
                </div>

                {/* Stats Summary inside completion modal */}
                <div
                    className="w-100 rounded-3 p-3 d-flex flex-column gap-1 text-start"
                    style={{
                        backgroundColor: hasTicket ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.15)',
                    }}
                >
                    <div className="d-flex align-items-center justify-content-between">
                        <span className={`small ${hasTicket ? 'text-dark' : 'text-white'}`}>Gefundene Paare:</span>
                        <span className={`small fw-bold ${hasTicket ? 'text-dark' : 'text-white'}`}>
                            {matchedPairsCount}/{totalPairsCount}
                        </span>
                    </div>
                    <div className="d-flex align-items-center justify-content-between">
                        <span className={`small ${hasTicket ? 'text-dark' : 'text-white'}`}>Versuche:</span>
                        <span className={`small fw-bold ${hasTicket ? 'text-dark' : 'text-white'}`}>
                            {String(attempts).padStart(3, '0')}
                        </span>
                    </div>
                </div>

                {hasLink && (
                    <Button
                        href={destinationUrl}
                        tag="a"
                        target={linkTarget}
                        rel={linkRel}
                        color={hasTicket ? 'white' : 'yellow'}
                        className="w-100 fw-bold py-2 anl_memory-game--final-link-btn"
                        style={buttonStyle}
                    >
                        <span className="d-flex align-items-center justify-content-center gap-1">
                            {buttonConfig?.text || 'Weiter'}
                            {isExternal && <IconExternalLink size={16} />}
                        </span>
                    </Button>
                )}
            </ModalBody>
        </Modal>
    );
};

export default WinModal;
